import { Reveal } from "./Reveal";
import { LOGO_ICON } from "../../lib/content";
import { QrCode, Wallet, Copy, ShieldCheck } from "lucide-react";

const cells = (() => {
  const out = [];
  let seed = 7;
  const inFinder = (r, c) => (r < 7 && c < 7) || (r < 7 && c > 13) || (r > 13 && c < 7);
  for (let r = 0; r < 21; r++)
    for (let c = 0; c < 21; c++) {
      seed = (seed * 31 + r * 17 + c * 13) % 97;
      if (!inFinder(r, c) && seed % 2 === 0) out.push([r, c]);
    }
  return out;
})();

const Finder = ({ x, y }) => (
  <g>
    <rect x={x} y={y} width="7" height="7" fill="#0A0C14" />
    <rect x={x + 1} y={y + 1} width="5" height="5" fill="#fff" />
    <rect x={x + 2} y={y + 2} width="3" height="3" fill="#0A0C14" />
  </g>
);

const FakeQr = () => (
  <svg viewBox="0 0 21 21" className="h-36 w-36 sm:h-44 sm:w-44" role="img" aria-label="Payment QR code">
    <rect width="21" height="21" fill="#fff" />
    {cells.map(([r, c]) => (
      <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#0A0C14" />
    ))}
    <Finder x={0} y={0} />
    <Finder x={14} y={0} />
    <Finder x={0} y={14} />
  </svg>
);

const chains = [
  { name: "Base", meta: "$0.02 · ~4s", active: true },
  { name: "Tron", meta: "$0.90 · ~60s" },
  { name: "Polygon", meta: "$0.01 · ~5s" },
  { name: "Solana", meta: "$0.01 · ~2s" },
];

const tabs = [
  { label: "QR code", icon: QrCode, active: true },
  { label: "Wallet", icon: Wallet },
  { label: "Copy address", icon: Copy },
];

export const CheckoutDemo = () => (
  <section id="checkout" className="bg-[#FAFAFB] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="overline mb-4">Merchant checkout</p>
        <h2 className="font-heading max-w-2xl text-3xl font-medium tracking-tight text-[#0A0C14] sm:text-4xl lg:text-5xl">
          Your brand. <span className="text-slate-400">Our rails.</span>
        </h2>
        <p className="mt-5 max-w-xl text-base text-slate-600 lg:text-lg">
          A glimpse of the hosted checkout your customers will see — branded, mobile-first, and paid in seconds.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-14 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_30px_80px_rgba(10,12,20,0.08)]" data-testid="checkout-demo">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-[#FAFAFB] px-5 py-3.5">
            <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
            <span className="ml-4 rounded-full bg-white px-4 py-1 font-mono text-[11px] text-slate-400 shadow-sm">pay.acmestudio.com · secured by Nomoex Pay</span>
          </div>
          <div className="grid lg:grid-cols-5">
            <div className="noise-overlay relative bg-[#0A0C14] p-8 lg:col-span-2 lg:p-12">
              <div className="flex items-center gap-3">
                <span className="font-heading flex h-10 w-10 items-center justify-center rounded-xl bg-[#3B5FE8] text-lg font-semibold text-white">A</span>
                <div>
                  <p className="text-sm font-semibold text-white">Acme Studio</p>
                  <p className="text-xs text-slate-500">Order #1042</p>
                </div>
              </div>
              <div className="mt-10">
                <p className="text-xs uppercase tracking-widest text-slate-500">Amount due</p>
                <p className="font-heading mt-2 text-5xl font-medium tracking-tight text-white">$1,250.00</p>
                <p className="font-mono mt-2 text-xs text-slate-400">≈ 1,250.38 USDC · rate locked <span className="text-emerald-400">14:32</span></p>
              </div>
              <div className="mt-10 space-y-3 border-t border-white/10 pt-6 text-sm">
                <div className="flex justify-between text-slate-400"><span>Brand design sprint</span><span className="font-mono">$1,150.00</span></div>
                <div className="flex justify-between text-slate-400"><span>Rush delivery</span><span className="font-mono">$100.00</span></div>
              </div>
              <p className="mt-12 flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck size={14} className="text-[#7B96FF]" /> KYT-screened · Travel Rule compliant
              </p>
            </div>

            <div className="p-8 lg:col-span-3 lg:p-12">
              <div className="flex flex-wrap gap-2" data-testid="checkout-tabs">
                {tabs.map((t) => (
                  <span key={t.label} className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold ${t.active ? "bg-[#0A0C14] text-white" : "border border-slate-200 text-slate-500"}`}>
                    <t.icon size={13} /> {t.label}
                  </span>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-2" data-testid="checkout-chains">
                {chains.map((c) => (
                  <span key={c.name} className={`rounded-xl border px-4 py-2 text-xs ${c.active ? "border-[#3B5FE8] bg-[#E8EDFF]/60 text-[#2A46B5]" : "border-slate-200 text-slate-500"}`}>
                    <span className="font-semibold">{c.name}</span> <span className="font-mono ml-1 opacity-70">{c.meta}</span>
                  </span>
                ))}
              </div>
              <div className="mt-8 flex flex-col items-center gap-6 sm:flex-row sm:gap-10">
                <div className="rounded-2xl border border-slate-200 p-4 shadow-[0_8px_30px_rgba(10,12,20,0.05)]">
                  <FakeQr />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">Scan with any wallet</p>
                  <p className="mt-1.5 text-sm text-slate-500">Send exactly 1,250.38 USDC on Base. We detect it the moment it hits the mempool.</p>
                  <div className="mt-5 flex items-center gap-2.5 rounded-xl bg-[#0A0C14] px-4 py-3 text-sm text-white" data-testid="checkout-status">
                    <span className="pulse-dot h-2 w-2 rounded-full bg-emerald-400" />
                    Waiting for payment · detected → confirming → complete
                  </div>
                  <p className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-400">
                    <img src={LOGO_ICON} alt="" className="h-3.5 w-3.5" /> Powered by Nomoex Pay · refunds &amp; receipts built in
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
